import { useState, useEffect, useRef } from "react";
import { MagnifyingGlass, MapPin, CrosshairSimple, FunnelSimple, DownloadSimple, Briefcase, ArrowSquareOut, Prohibit, Database, UploadSimple, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, SectionTitle } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/DatePicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ComparablesMap, { reverseGeocode } from "@/components/ComparablesMap";
import HistoricalAnalysis from "@/components/HistoricalAnalysis";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PROPERTY_TYPES } from "@/lib/constants";

export default function Gis() {
  const [lat, setLat] = useState("");
  const [lng, setLng] = useState("");
  const [radius, setRadius] = useState("3");
  const [uom, setUom] = useState("km");
  const toKm = () => { const r = parseFloat(radius) || 3; return uom === "m" ? r / 1000 : uom === "mi" ? r * 1.60934 : r; };
  const [bank, setBank] = useState("");
  const [ptype, setPtype] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [selected, setSelected] = useState({});
  const [attachJob, setAttachJob] = useState("");
  const [pdfBusy, setPdfBusy] = useState(false);
  const [address, setAddress] = useState("");
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [legacy, setLegacy] = useState([]);
  const [legacyBusy, setLegacyBusy] = useState(false);
  const [showLegacy, setShowLegacy] = useState(false);
  const fileRef = useRef(null);
  const navigate = useNavigate();
  const openJob = (id) => navigate(`/app/jobs?edit=${id}`);

  const loadLegacy = () => api.get("/comparables/legacy")
    .then(({ data }) => setLegacy(Array.isArray(data.records) ? data.records : []))
    .catch(() => {});

  useEffect(() => { loadLegacy(); }, []);

  const undoBatch = async (batchId) => {
    try { const { data } = await api.delete(`/comparables/legacy/batch/${batchId}`); toast.success(`Reverted ${data.deleted} imported record(s)`); loadLegacy(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const uploadLegacy = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setLegacyBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/comparables/legacy/upload", fd);
      const bad = data.skipped ? `, skipped ${data.skipped} bad row(s)` : "";
      const dup = data.duplicates ? `, skipped ${data.duplicates} duplicate(s)` : "";
      toast.success(`Imported ${data.imported} record(s)${bad}${dup}`,
        data.batch_id && data.imported ? { duration: 10000, action: { label: "Undo", onClick: () => undoBatch(data.batch_id) } } : undefined);
      if (data.reasons?.length) data.reasons.forEach((r) => toast.info(r));
      setShowLegacy(true);
      loadLegacy();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLegacyBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const deleteLegacy = async (id) => {
    try { await api.delete(`/comparables/legacy/${id}`); setLegacy((l) => l.filter((r) => r.id !== id)); toast.success("Record deleted"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const clearLegacy = async () => {
    if (!window.confirm("Delete ALL imported legacy records? This cannot be undone.")) return;
    try { const { data } = await api.delete("/comparables/legacy"); setLegacy([]); toast.success(`Deleted ${data.deleted} record(s)`); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const downloadLegacyTemplate = () => {
    const head = "Latitude,Longitude,Area,Month,Rate,Property Type,Reference/ID,Bank Name,Address";
    const sample = '21.2514,81.6296,1200,2023-05,4500,Residential,REF-001,SBI,"Shankar Nagar, Raipur"';
    const blob = new Blob(["\ufeff" + head + "\n" + sample + "\n"], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "legacy-comparables-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    api.get("/geo/coorded-jobs").then(({ data }) => setJobs(Array.isArray(data) ? data : [])).catch(() => {});
  }, []);

  const search = async (la = parseFloat(lat), lo = parseFloat(lng)) => {
    if (Number.isNaN(la) || Number.isNaN(lo)) { toast.error("Enter a valid latitude and longitude"); return; }
    setLoading(true);
    try {
      const body = { lat: la, lon: lo, radius_km: toKm() };
      if (bank.trim()) body.bank_name = bank.trim();
      if (ptype.trim()) body.property_type = ptype.trim();
      if (dateFrom) body.date_from = dateFrom;
      if (dateTo) body.date_to = dateTo;
      const { data } = await api.post("/geo/historical-comparables", body);
      setResult(data);
      if (!data.points.length) toast.info("No historical valuations match these filters");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const pickJob = (id) => {
    const j = jobs.find((x) => x.id === id);
    if (!j) return;
    setLat(String(j.lat)); setLng(String(j.lng));
    search(j.lat, j.lng);
  };

  const onPaste = (e) => {
    const t = (e.clipboardData || window.clipboardData).getData("text");
    const m = t.match(/(-?\d+\.\d+)\s*,\s*(-?\d+\.\d+)/);
    if (m) { e.preventDefault(); setLat(m[1]); setLng(m[2]); }
  };

  const exportCsv = () => {
    const rows = result?.points || [];
    if (!rows.length) return;
    const head = ["Reference", "Applicant", "Bank", "Property Type", "Rate per sqft", "Area sqft", "FMV", "Date", "Distance km", "Status", "Latitude", "Longitude"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [head.join(",")].concat(rows.map((p) => [p.reference, p.applicant_name, p.bank_name, p.property_type, p.rate, p.area, p.fmv, p.date, p.distance_km, p.status, p.lat, p.lng].map(esc).join(",")));
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `historical-comparables-${result.center.lat}_${result.center.lng}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Comparables exported");
  };

  const pickOnMap = (la, lo) => {
    setLat(la.toFixed(6)); setLng(lo.toFixed(6)); search(la, lo);
    setAddress("Locating address…");
    reverseGeocode(la, lo).then((a) => setAddress(a || "")).catch(() => setAddress(""));
  };

  const lastBody = () => {
    const body = { lat: parseFloat(lat), lon: parseFloat(lng), radius_km: toKm() };
    if (bank.trim()) body.bank_name = bank.trim();
    if (ptype.trim()) body.property_type = ptype.trim();
    if (dateFrom) body.date_from = dateFrom;
    if (dateTo) body.date_to = dateTo;
    return body;
  };

  const downloadPdf = async () => {
    setPdfBusy(true);
    try {
      const res = await api.post("/geo/historical-comparables/pdf", lastBody(), { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "comparable-rate-analysis.pdf"; a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setPdfBusy(false); }
  };

  const toggleSel = (id) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  const attach = async () => {
    if (!attachJob) { toast.error("Pick a job to attach to"); return; }
    const chosen = (result?.points || []).filter((p) => selected[p.id]);
    if (!chosen.length) { toast.error("Select at least one comparable"); return; }
    try {
      await api.post(`/jobs/${attachJob}/attach-comparables`, { comparables: chosen });
      toast.success(`Attached ${chosen.length} comparable(s) to the job`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const toggleBlacklist = async (p) => {
    try {
      await api.post(`/jobs/${p.id}/blacklist`, { blacklisted: !p.blacklisted });
      toast.success(p.blacklisted ? "Removed from caution list" : "Flagged as caution / black-listed");
      search();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const subject = result ? { lat: result.center.lat, lon: result.center.lng } : null;
  const listings = (result?.points || []).map((p) => ({
    lat: p.lat, lng: p.lng,
    title: `${p.reference || ""}${p.applicant_name ? " · " + p.applicant_name : ""}`,
    rate: p.rate, area: p.area, source: p.bank_name || "Firm valuation", distance: `${p.distance_km} km`,
    ptype: p.property_type,
    blacklisted: p.blacklisted,
  }));

  return (
    <div className="space-y-5" data-testid="gis-page">
      <PageHeader overline="03 · Historical Comparable" title="Historical Valuation Map" subtitle="Search your past valuations by latitude & longitude and see the rate heat-map around any point" />

      <Card className="p-4 border-border space-y-3">
        <SectionTitle icon={CrosshairSimple}>Search by coordinates</SectionTitle>
        <div className="grid sm:grid-cols-5 gap-3">
          <div className="space-y-1"><Label className="text-xs">Latitude</Label><Input value={lat} onChange={(e) => setLat(e.target.value)} onPaste={onPaste} placeholder="21.2514" data-testid="gis-lat-input" /></div>
          <div className="space-y-1"><Label className="text-xs">Longitude</Label><Input value={lng} onChange={(e) => setLng(e.target.value)} onPaste={onPaste} placeholder="81.6296" data-testid="gis-lng-input" /></div>
          <div className="space-y-1"><Label className="text-xs">Radius</Label><Input type="number" min="0.5" step="0.5" value={radius} onChange={(e) => setRadius(e.target.value)} data-testid="gis-radius-input" /></div>
          <div className="space-y-1"><Label className="text-xs">Unit</Label>
            <Select value={uom} onValueChange={setUom}>
              <SelectTrigger data-testid="gis-uom-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="km">km</SelectItem>
                <SelectItem value="m">meters</SelectItem>
                <SelectItem value="mi">miles</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end"><Button onClick={() => search()} disabled={loading} className="rounded-full w-full bg-primary hover:bg-[#113A64]" data-testid="gis-search-btn"><MagnifyingGlass size={16} className="mr-1" /> {loading ? "Searching…" : "Search"}</Button></div>
        </div>

        <div className="space-y-1">
          <Label className="text-xs flex items-center gap-1"><Briefcase size={13} /> Use a past job's location</Label>
          <Select value="" onValueChange={pickJob}>
            <SelectTrigger data-testid="gis-job-select"><SelectValue placeholder={jobs.length ? "Pick a job with saved coordinates…" : "No jobs with coordinates yet"} /></SelectTrigger>
            <SelectContent>
              {jobs.map((j) => (
                <SelectItem key={j.id} value={j.id} data-testid={`gis-job-${j.id}`}>{j.reference || "—"} · {j.applicant_name || "Unnamed"}{j.bank_name ? ` · ${j.bank_name}` : ""}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="border-t border-border pt-3">
          <div className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5 mb-2"><FunnelSimple size={14} className="text-accent" /> Filters (optional)</div>
          <div className="grid sm:grid-cols-4 gap-3">
            <div className="space-y-1"><Label className="text-xs">Bank</Label><Input value={bank} onChange={(e) => setBank(e.target.value)} placeholder="e.g. SBI" data-testid="gis-bank-filter" /></div>
            <div className="space-y-1"><Label className="text-xs">Data type</Label>
              <Select value={ptype || "all"} onValueChange={(v) => setPtype(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="gis-type-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Data</SelectItem>
                  {PROPERTY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label className="text-xs">Valued from</Label><DatePicker value={dateFrom} onChange={setDateFrom} placeholder="Valued from" clearable className="w-full" data-testid="gis-date-from" /></div>
            <div className="space-y-1"><Label className="text-xs">Valued to</Label><DatePicker value={dateTo} onChange={setDateTo} placeholder="Valued to" clearable className="w-full" data-testid="gis-date-to" /></div>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">Tip: paste a "lat, lng" pair from Google Maps into either box. Click Search to apply filters.</p>
        </div>

        {address && (
          <div className="flex items-start gap-1.5 text-[11px] text-muted-foreground border-t border-border pt-3" data-testid="gis-pin-address">
            <MapPin size={13} className="text-accent mt-0.5 shrink-0" />
            <span><span className="font-semibold text-primary">Nearest address:</span> {address}</span>
          </div>
        )}
      </Card>

      <Card className="p-4 border-border space-y-3" data-testid="legacy-card">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <SectionTitle icon={Database}>Legacy records {legacy.length ? `(${legacy.length})` : ""}</SectionTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" size="sm" className="rounded-full" onClick={downloadLegacyTemplate} data-testid="legacy-template-btn"><DownloadSimple size={15} className="mr-1" /> Template</Button>
            {legacy.length > 0 && (
              <Button variant="outline" size="sm" className="rounded-full" onClick={() => setShowLegacy((v) => !v)} data-testid="legacy-toggle-btn">{showLegacy ? "Hide" : "View / manage"}</Button>
            )}
            {isOwner && (
              <>
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={uploadLegacy} className="hidden" data-testid="legacy-file-input" />
                <Button size="sm" className="rounded-full bg-primary hover:bg-[#113A64]" disabled={legacyBusy} onClick={() => fileRef.current?.click()} data-testid="legacy-upload-btn"><UploadSimple size={15} className="mr-1" /> {legacyBusy ? "Importing…" : "Upload CSV / Excel"}</Button>
              </>
            )}
          </div>
        </div>
        <p className="text-[11px] text-muted-foreground">
          Import old valuations from a CSV / Excel sheet. Columns: <span className="font-medium text-primary">Latitude, Longitude, Rate</span> (required) and Area, Month, Property Type, Reference/ID, Bank Name, Address (optional). Imported records appear in every comparable search, the rate heat-map, PDFs (as G1, G2… pins) and the per-job 2 km comparables. Bad rows and duplicate rows are skipped automatically on re-upload.
        </p>
        {showLegacy && legacy.length > 0 && (
          <div className="border border-border rounded-lg overflow-hidden">
            <div className="flex items-center justify-between px-3 py-2 bg-muted/40">
              <span className="text-xs font-semibold text-muted-foreground">{legacy.length} imported record(s)</span>
              {isOwner && <button type="button" onClick={clearLegacy} className="text-[11px] text-red-600 hover:underline flex items-center gap-1" data-testid="legacy-clear-btn"><Trash size={12} /> Clear all</button>}
            </div>
            <div className="max-h-72 overflow-auto divide-y divide-border" data-testid="legacy-list">
              {legacy.map((r) => (
                <div key={r.id} className="px-3 py-2 flex items-center justify-between gap-3" data-testid={`legacy-row-${r.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-primary truncate">{r.reference || "—"}{r.property_type ? ` · ${r.property_type}` : ""}{r.bank_name ? ` · ${r.bank_name}` : ""}</div>
                    <div className="text-[11px] text-muted-foreground truncate">{r.latitude}, {r.longitude}{r.property_address ? ` · ${r.property_address}` : ""}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-semibold text-primary">{r.rate ? `₹${Number(r.rate).toLocaleString("en-IN")}/sqft` : "—"}</div>
                    <div className="text-[11px] text-muted-foreground">{r.area ? `${r.area} sqft` : ""}{r.month ? ` · ${r.month}` : ""}</div>
                  </div>
                  {isOwner && <button type="button" onClick={() => deleteLegacy(r.id)} title="Delete record" className="shrink-0 text-muted-foreground hover:text-red-600" data-testid={`legacy-delete-${r.id}`}><Trash size={15} /></button>}
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>

      {result && (
        <>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-sm text-muted-foreground" data-testid="gis-result-count">
              <MapPin size={16} className="text-accent" /> {result.count} historical valuation{result.count === 1 ? "" : "s"} within {result.radius_km} km
            </div>
            <Button variant="outline" size="sm" className="rounded-full" onClick={downloadPdf} disabled={pdfBusy || !result.points.length} data-testid="gis-pdf-btn"><DownloadSimple size={15} className="mr-1" /> {pdfBusy ? "Preparing…" : "PDF"}</Button>
            <Button variant="outline" size="sm" className="rounded-full" onClick={exportCsv} disabled={!result.points.length} data-testid="gis-export-btn"><DownloadSimple size={15} className="mr-1" /> Export CSV</Button>
          </div>
          <Card className="p-0 overflow-hidden border-border">
            <ComparablesMap subject={subject} listings={listings} onPick={pickOnMap} colorBy="type" />
          </Card>
          <p className="text-[11px] text-muted-foreground -mt-2">Tip: click anywhere on the map, or drag the blue Subject pin, to set the search point.</p>
          {Object.values(selected).some(Boolean) && (
            <Card className="p-3 border-border flex flex-wrap items-end gap-3" data-testid="gis-attach-bar">
              <div className="space-y-1 flex-1 min-w-[200px]">
                <Label className="text-xs">Attach {Object.values(selected).filter(Boolean).length} selected comparable(s) to job</Label>
                <Select value={attachJob} onValueChange={setAttachJob}>
                  <SelectTrigger data-testid="gis-attach-job"><SelectValue placeholder="Pick a job…" /></SelectTrigger>
                  <SelectContent>
                    {jobs.map((j) => (<SelectItem key={j.id} value={j.id}>{j.reference || "—"} · {j.applicant_name || "Unnamed"}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <Button className="rounded-full bg-primary hover:bg-[#113A64]" onClick={attach} data-testid="gis-attach-btn">Attach to job</Button>
            </Card>
          )}
          <HistoricalAnalysis points={result.points} onOpenJob={openJob} />
          <Card className="p-0 border-border overflow-hidden">
            <div className="max-h-80 overflow-auto divide-y divide-border" data-testid="gis-result-list">
              {result.points.length === 0 && <p className="text-sm text-muted-foreground p-4">No historical valuations match these filters. Try a wider radius or clear the filters.</p>}
              {result.points.map((p) => (
                <div key={p.id} className="px-4 py-2.5 flex items-center justify-between gap-3" data-testid={`gis-point-${p.id}`}>
                  <input type="checkbox" checked={!!selected[p.id]} onChange={() => toggleSel(p.id)} className="shrink-0 h-4 w-4 accent-[#0A2540]" data-testid={`gis-select-${p.id}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-primary truncate">{p.reference || "—"}{p.applicant_name ? ` · ${p.applicant_name}` : ""}{p.is_legacy ? <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-accent/20 text-accent">Legacy</span> : null}{p.blacklisted ? <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-gray-900 text-white">Caution</span> : null}</div>
                    <div className="text-xs text-muted-foreground truncate">{p.bank_name || ""}{p.property_type ? ` · ${p.property_type}` : ""}{p.property_address ? ` · ${p.property_address}` : ""}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-semibold text-primary">{p.rate ? `₹${Number(p.rate).toLocaleString("en-IN")}/sqft` : "—"}</div>
                    <div className="text-[11px] text-muted-foreground">{p.distance_km} km{p.date ? ` · ${p.date}` : ""}{p.status ? ` · ${String(p.status).replace(/_/g, " ")}` : ""}</div>
                  </div>
                  {p.is_legacy ? null : (
                    <>
                      <button type="button" onClick={() => toggleBlacklist(p)} title={p.blacklisted ? "Remove caution flag" : "Flag as caution / black-listed"} className={`shrink-0 ${p.blacklisted ? "text-gray-900" : "text-muted-foreground hover:text-gray-900"}`} data-testid={`gis-blacklist-${p.id}`}><Prohibit size={16} /></button>
                      <button type="button" onClick={() => openJob(p.id)} title="Open this job" className="shrink-0 text-accent hover:text-primary" data-testid={`gis-open-${p.id}`}><ArrowSquareOut size={16} /></button>
                    </>
                  )}
                </div>
              ))}
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
